// Standalone inline product panel. Does not replace product-card.js or quick-add.js.
import { ThemeEvents } from '@theme/events';

// Trial implementation: fetches product-page main content and uses native theme components.
// It intentionally does not embed a second header/footer or re-run inline app scripts.
let nextAccordionId = 0;
const loadedModules = new Map();

async function loadThemeModules(html, loaderUrl) {
  const assetRoot = new URL('.', new URL(loaderUrl, location.href)).href;
  const moduleScripts = Array.from(html.querySelectorAll('script[type="module"][src]'));
  await Promise.all(moduleScripts.map(async (source) => {
    const url = new URL(source.getAttribute('src'), location.href).href;
    if (!url.startsWith(assetRoot)) return;
    if (loadedModules.has(url)) { await loadedModules.get(url); return; }
    const existing = Array.from(document.querySelectorAll('script[src]')).some(script => script.src === url);
    if (existing) return;
    if (!loadedModules.has(url)) {
      loadedModules.set(url, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = url;
        script.onload = resolve;
        script.onerror = () => { loadedModules.delete(url); script.remove(); reject(new Error('A product component could not load.')); };
        document.head.appendChild(script);
      }));
    }
    await loadedModules.get(url);
  }));
}

class ProductCardAccordion extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.card = this.closest('product-card');
    this.panel = this.querySelector('[data-accordion-collapse]');
    this.content = this.querySelector('[data-accordion-page]');
    this.status = this.querySelector('[data-accordion-status]');
    if (!this.card || !this.panel || !this.content) return;
    this.panel.id = `inline-product-page-${++nextAccordionId}`;
    this.opened = false;
    this.configureTriggers();
    this.observer = new MutationObserver(() => this.configureTriggers());
    this.observer.observe(this.card, {childList:true, subtree:true});
    this.card.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-product-accordion-toggle]');
      if (!trigger || trigger.closest('product-card') !== this.card) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.opened ? this.close() : this.open();
    }, {capture:true, signal});
    this.panel.addEventListener('click', event => {
      // This panel now sits outside the card. Let Aris receive normal bubbled clicks.
      if (!(event.target instanceof Element)) return;
      const close = event.target.closest('[data-accordion-close]');
      if (close && this.panel.contains(close)) {
        event.preventDefault();
        this.close(true);
      }
    }, {signal});
    this.panel.addEventListener(ThemeEvents.variantSelected, event => {
      const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
      if (!picker) return;
      picker.dataset.templateProductMatch = 'false';
      if (!picker.__inlineRequestWrapped && typeof picker.buildRequestUrl === 'function') {
        const build = picker.buildRequestUrl.bind(picker);
        const themeId = this.dataset.themeId;
        picker.buildRequestUrl = (...args) => {
          const url = new URL(build(...args), location.origin);
          if (themeId) url.searchParams.set('preview_theme_id', themeId);
          return url.href;
        };
        picker.__inlineRequestWrapped = true;
      }
    }, {capture:true, signal});
    // Native product components receive these events first; the collection card must not handle them.
    for (const type of [ThemeEvents.variantUpdate, ThemeEvents.variantSelected]) {
      this.panel.addEventListener(type, event => {
        event.stopPropagation();
        if (type === ThemeEvents.variantUpdate && this.arisApplied) {
          setTimeout(() => {
            if (this.opened && !this.arisLoading && !this.content.querySelector('.avpoptions-container__v2 .avp-option') && this.lastProductUrl) {
              this.initialiseAris(this.lastProductUrl, this.fetchController?.signal);
            }
          }, 0);
        }
      }, {signal});
    }
    this.panel.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.opened) {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      }
    }, {signal});
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.fetchController?.abort();
    this.observer?.disconnect();
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.openFrame);
    this.resizeBound = false;
    if (this.row) {
      if (this.panel) this.appendChild(this.panel);
      this.row.remove();
      this.row = null;
    }
  }

  configureTriggers() {
    this.triggers = Array.from(this.card.querySelectorAll('[data-product-accordion-toggle]'))
      .filter(trigger => trigger.closest('product-card') === this.card);
    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-controls', this.panel.id);
      trigger.setAttribute('aria-expanded', String(this.opened));
    }
  }

  async open() {
    for (const accordion of document.querySelectorAll('product-card-accordion')) {
      if (accordion !== this && accordion.opened) accordion.close(false, true);
    }
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.openFrame);
    try {
      this.mountRow();
    } catch (error) {
      this.opened = true;
      this.panel.hidden = false;
      this.panel.inert = false;
      this.panel.classList.add('is-open');
      this.configureTriggers();
      if (this.status) this.status.textContent = 'The product panel could not be placed in this layout. Open the product page below.';
      const fallback = this.panel.querySelector('[data-accordion-fallback]');
      if (fallback) fallback.hidden = false;
      console.error('Inline product placement:', error);
      return;
    }
    this.opened = true;
    this.panel.hidden = false;
    this.panel.inert = false;
    this.configureTriggers();
    void this.panel.offsetHeight;
    this.openFrame = requestAnimationFrame(() => this.panel.classList.add('is-open'));
    this.panel.querySelector('[data-accordion-close]')?.focus({preventScroll:true});
    this.panel.scrollIntoView({block:'start', behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    if (!this.content.childElementCount) await this.loadPage();
  }

  close(returnFocus = false, immediate = false) {
    cancelAnimationFrame(this.openFrame);
    clearTimeout(this.closeTimer);
    this.fetchController?.abort();
    this.opened = false;
    const focusWasInside = this.panel.contains(document.activeElement);
    this.panel.classList.remove('is-open');
    this.panel.inert = true;
    this.configureTriggers();
    if (returnFocus || focusWasInside) this.triggers[0]?.focus();
    const finish = () => {
      if (this.opened) return;
      this.panel.hidden = true;
      this.content.replaceChildren();
      if (this.status) this.status.textContent = '';
      this.appendChild(this.panel);
      this.row?.remove();
      this.row = null;
    };
    if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else this.closeTimer = setTimeout(finish, 300);
  }

  mountRow() {
    if (this.row?.isConnected) { this.sizeRow(); return; }
    const list = this.card.closest('.resource-list, .product-grid');
    const carousel = this.card.closest('slideshow-component, slideshow-slide');
    let parent;
    let anchor;
    if (list && !carousel && getComputedStyle(list).display.includes('grid')) {
      // Product-list cards are wrapped in div.resource-list__item, not li.
      // Insert into the actual grid, never into that individual wrapper.
      let item = this.card;
      while (item.parentElement && item.parentElement !== list) item = item.parentElement;
      if (item.parentElement !== list) throw new Error('The product grid item could not be located.');
      parent = list;
      anchor = item;
      const top = item.getBoundingClientRect().top;
      const items = Array.from(list.children).filter(node => !node.classList.contains('pc-accordion-row') && node.querySelector('product-card'));
      for (const sibling of items) {
        if (Math.abs(sibling.getBoundingClientRect().top - top) < 4) anchor = sibling;
      }
    } else if (list) {
      // Carousels/bento layouts clip their contents: put the panel beneath the list instead.
      parent = list.parentElement;
      anchor = list;
    } else {
      // Fallback for another genuine multi-product grid.
      let candidate = this.card.parentElement;
      while (candidate && !candidate.classList.contains('shopify-section')) {
        if (getComputedStyle(candidate).display.includes('grid') && candidate.querySelectorAll('product-card').length > 1) break;
        candidate = candidate.parentElement;
      }
      if (!candidate || candidate.classList.contains('shopify-section')) {
        throw new Error('A supported product list could not be located.');
      }
      let item = this.card;
      while (item.parentElement && item.parentElement !== candidate) item = item.parentElement;
      parent = candidate;
      anchor = item;
      const top = item.getBoundingClientRect().top;
      for (const sibling of Array.from(parent.children)) {
        if (!sibling.classList.contains('pc-accordion-row') && Math.abs(sibling.getBoundingClientRect().top - top) < 4) anchor = sibling;
      }
    }
    if (!parent || !anchor) throw new Error('Product panel placement could not be determined.');
    this.row = document.createElement(/^(UL|OL)$/.test(parent.tagName) ? 'li' : 'div');
    this.row.className = 'pc-accordion-row';
    anchor.after(this.row);
    this.row.appendChild(this.panel);
    this.sizeRow();
    if (!this.resizeBound) {
      this.resizeBound = true;
      window.addEventListener('resize', () => this.sizeRow(), {signal:this.controller.signal});
    }
  }

  sizeRow() {
    if (!this.row?.isConnected) return;
    const gutter = matchMedia('(max-width:749px)').matches ? 12 : 24;
    const viewport = document.documentElement.clientWidth;
    const left = this.row.getBoundingClientRect().left;
    this.row.style.setProperty('--pc-panel-width', `${Math.max(0, viewport - gutter * 2)}px`);
    this.row.style.setProperty('--pc-panel-offset', `${gutter - left}px`);
  }

  buildProductFragment(html) {
    const source = html.querySelector('[data-product-grid-content]');
    const sourceDetails = source?.querySelector('.product-details');
    const form = sourceDetails?.querySelector('product-form-component');
    const heading = sourceDetails?.querySelector('h1');
    if (!source || !sourceDetails || !form || !heading) throw new Error('The native product title or purchase form was not found.');
    const grid = source.cloneNode(true);
    const details = grid.querySelector('.product-details');
    const stock = details.querySelector('product-inventory');
    const titleHeading = details.querySelector('h1');
    const title = titleHeading.closest('.text-block') || titleHeading;
    const picker = details.querySelector('variant-picker');
    const purchase = details.querySelector('product-form-component');
    const stack = document.createElement('div');
    stack.className = 'pc-accordion__details-stack';
    if (stock) stack.appendChild(stock);
    stack.appendChild(title);
    if (picker) {
      picker.dataset.templateProductMatch = 'false';
      stack.appendChild(picker);
    }
    // Retain hidden/native quantity and error handling needed by the theme form.
    // Presentation CSS hides quantity and accelerated checkout, leaving the buy button.
    purchase.querySelectorAll('shopify-payment-terms, .shopify-payment-button, shopify-accelerated-checkout, local-pickup, volume-pricing').forEach(node => node.remove());
    stack.appendChild(purchase);
    details.replaceChildren(stack);
    details.classList.remove('sticky-content--desktop', 'full-height--desktop');
    for (const child of Array.from(grid.children)) {
      if (!child.matches('.product-information__media, .product-details')) child.remove();
    }
    const sectionSource = source.closest('.shopify-section');
    const root = sectionSource ? sectionSource.cloneNode(false) : document.createElement('div');
    root.classList.add('shopify-section', 'pc-native-product-root');
    const information = source.closest('.product-information')?.cloneNode(false) || document.createElement('div');
    information.classList.add('product-information');
    information.appendChild(grid);
    root.appendChild(information);
    root.querySelectorAll('script:not([type="application/json"]), product-card-accordion').forEach(node => node.remove());
    return root;
  }

  async initialiseAris(productUrl, signal) {
    if (!this.opened || signal?.aborted || (this.arisLoading && this.arisSignal === signal)) return;
    const arisRun = Symbol('aris-run');
    this.arisRun = arisRun;
    this.arisSignal = signal;
    this.arisLoading = true;
    this.arisApplied = false;
    this.content.inert = true;
    this.content.setAttribute('aria-busy', 'true');
    if (this.status) this.status.textContent = 'Checking product options…';
    const deadline = Date.now() + 15000;
    const active = () => this.arisRun === arisRun && this.opened && !signal?.aborted && this.isConnected;
    const waitFor = async test => {
      while (active() && Date.now() < deadline) {
        const value = test();
        if (value) return value;
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!active()) return null;
      throw new Error('Aris product options did not become ready.');
    };
    let timeout;
    try {
      await waitFor(() => typeof window.checkAvisOption === 'function' &&
        typeof window.AvisOptionsData?.pluginAction?.getProduct === 'function' &&
        typeof window.AvisOptionsData?.mainCheckAllRules === 'function');
      if (!active()) return;
      const url = new URL(productUrl, location.origin);
      const handle = decodeURIComponent(url.pathname.split('/products/')[1].split('/')[0]);
      const remaining = Math.max(1, deadline - Date.now());
      const product = await Promise.race([
        window.AvisOptionsData.pluginAction.getProduct(handle),
        new Promise((resolve, reject) => { timeout = setTimeout(() => reject(new Error('Aris product lookup timed out.')), remaining); })
      ]);
      clearTimeout(timeout);
      if (!active()) return;
      if (!product?.id) throw new Error('Aris could not identify this product.');
      const applies = await window.checkAvisOption(product);
      if (!active()) return;
      // The supplied example is known to use Aris. Fail closed if the app's rules disagree.
      if (!applies && String(product.id) === '9319561036002') {
        throw new Error('Aris did not identify the expected option set for this bracelet kit.');
      }
      if (applies) {
        this.arisApplied = true;
        const root = this.content.querySelector('.pc-native-product-root');
        const form = root?.querySelector('form[action*="/cart/add"]');
        if (!root || !form) throw new Error('The quick-view purchase form is missing.');
        // This is the same call used by Aris's own exported quick-view initializer
        // on non-product pages. Do not reset its globals or reload its scripts.
        await Promise.resolve(window.AvisOptionsData.mainCheckAllRules(
          product, product.id + '_quickview', false, root, null
        ));
        if (typeof window.APO_APPEND_PRICE === 'function') window.APO_APPEND_PRICE(root, product);
        await waitFor(() => root.querySelector('.avpoptions-container__v2 .avp-option'));
        if (!active()) return;
      }
      this.content.inert = false;
      if (this.status) this.status.textContent = '';
      const fallback = this.panel.querySelector('[data-accordion-fallback]');
      if (fallback) fallback.hidden = true;
    } catch (error) {
      if (!active()) return;
      // Never allow a purchase from this panel when required app options could not initialize.
      this.content.inert = true;
      if (this.status) this.status.textContent = 'Product options could not be verified here. Please select your options on the product page.';
      const fallback = this.panel.querySelector('[data-accordion-fallback]');
      if (fallback) fallback.hidden = false;
      console.error('Inline Aris options:', error);
    } finally {
      clearTimeout(timeout);
      if (this.arisRun === arisRun) this.arisLoading = false;
      if (this.fetchController?.signal === signal) this.content.removeAttribute('aria-busy');
    }
  }

  async loadPage() {
    this.fetchController?.abort();
    const controller = new AbortController();
    this.fetchController = controller;
    this.content.setAttribute('aria-busy', 'true');
    if (this.status) this.status.textContent = 'Loading product details…';
    try {
      const cardUrl = this.card.getProductCardLink?.()?.href;
      const url = new URL(cardUrl || this.dataset.productUrl, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) {
        throw new Error('A valid same-store product URL is required.');
      }
      // Keep fetching the theme where this panel is installed, including unpublished theme previews.
      if (this.dataset.themeId) url.searchParams.set('preview_theme_id', this.dataset.themeId);
      url.searchParams.delete('section_id');
      const response = await fetch(url.href, {signal:controller.signal, credentials:'same-origin'});
      if (!response.ok) throw new Error('Product page could not load.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const main = html.querySelector('main');
      if (!main || !main.querySelector('[data-product-grid-content]')) {
        throw new Error('Expected product-page content was not found.');
      }
      await loadThemeModules(html, this.dataset.loaderUrl);
      if (controller.signal.aborted || !this.opened) return;
      const productContent = this.buildProductFragment(html);
      this.content.replaceChildren(document.importNode(productContent, true));
      const fallback = this.panel.querySelector('[data-accordion-fallback]');
      if (fallback) fallback.hidden = true;
      this.sizeRow();
      this.lastProductUrl = url.href;
      await this.initialiseAris(url.href, controller.signal);
    } catch (error) {
      if (controller.signal.aborted) return;
      this.content.replaceChildren();
      if (this.status) this.status.textContent = 'These details could not load. Use “Open product page” below.';
      const fallback = this.panel.querySelector('[data-accordion-fallback]');
      if (fallback) fallback.hidden = false;
      console.error('Inline product page:', error);
    } finally {
      if (this.fetchController === controller) this.content.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('product-card-accordion')) customElements.define('product-card-accordion', ProductCardAccordion);